/**
 * Geolocation Middleware
 *
 * All side effects for geolocation are centralized here:
 * - navigator.geolocation.watchPosition calls
 * - sessionStorage persistence
 * - Permission API queries
 * - Timers (startup timeout, degradation)
 *
 * Key guardrails:
 * 1. Defensive time checks on init and visibility (don't rely on timers alone)
 * 2. Resume only from paused, never from error (prevents retry storms)
 * 3. Accuracy + distance + time throttling for dispatch
 */

import { MAX_PAUSED_AGE_MS, MAX_RESTORE_AGE_MS } from "@/constants/geolocation"
import type { GeoState, UserPosition, WatchPolicy } from "@/types/geolocation"

import { setCurrentPosition } from "@/lib/current-position"
import { nowMillis } from "@/lib/date-utils"
import * as geolocationSource from "@/lib/geolocation-source"
import { isClientSide } from "@/lib/is-client-side"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { webStorage } from "@/lib/web-storage"
import {
  geoStartRequested,
  geoStopRequested,
  geoVisibilityChanged,
} from "./geolocation-actions"
import { geolocationSlice } from "./geolocation-slice"
import { resetInitGeoMemo } from "./geolocation-thunks"
import type { AppDispatch, RootState } from "./redux-store"

// Configuration constants
const STARTUP_TIMEOUT_MS = 15 * 1000 // 15 seconds
const DISTANCE_THRESHOLD_M = 25 // meters
const ACCURACY_IMPROVEMENT_THRESHOLD = 0.5 // 50% improvement
const THROTTLE_MS = 5000 // 5 seconds

export const geoMiddleware = createMtListenerMiddleware(
  "geolocation-middleware"
)

// Scoped state (reset on watch stop)
let currentWatchId: number | null = null
let startupTimeoutId: ReturnType<typeof setTimeout> | null = null
let pausedDegradationId: ReturnType<typeof setTimeout> | null = null
let lastDispatchedPosition: UserPosition | null = null
let dispatchThrottleId: ReturnType<typeof setTimeout> | null = null
let persistThrottleId: ReturnType<typeof setTimeout> | null = null

function shouldArmStartupTimeout(getState: () => RootState): boolean {
  return getState().geolocation.status === "acquiring"
}

function armStartupTimeout(dispatch: AppDispatch) {
  if (startupTimeoutId !== null) return
  startupTimeoutId = setTimeout(() => {
    if (currentWatchId !== null) {
      geolocationSource.clearWatch(currentWatchId)
      currentWatchId = null
    }
    clearTimers()
    dispatch(
      geolocationSlice.actions.watchErrored({
        code: "TIMEOUT",
        message: "No GPS fix within timeout",
        at: nowMillis(),
      })
    )
  }, STARTUP_TIMEOUT_MS)
}

function clearTimers() {
  if (startupTimeoutId) {
    clearTimeout(startupTimeoutId)
    startupTimeoutId = null
  }
  if (pausedDegradationId) {
    clearTimeout(pausedDegradationId)
    pausedDegradationId = null
  }
  if (dispatchThrottleId) {
    clearTimeout(dispatchThrottleId)
    dispatchThrottleId = null
  }
  if (persistThrottleId) {
    clearTimeout(persistThrottleId)
    persistThrottleId = null
  }
}

/**
 * Haversine distance between two positions in meters
 */
function haversineDistance(p1: UserPosition, p2: UserPosition): number {
  const R = 6371000 // Earth radius in meters
  const lat1 = (p1.latitude * Math.PI) / 180
  const lat2 = (p2.latitude * Math.PI) / 180
  const dLat = ((p2.latitude - p1.latitude) * Math.PI) / 180
  const dLon = ((p2.longitude - p1.longitude) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Determine if we should dispatch immediately based on:
 * - First fix (no last position)
 * - Moved > 25m
 * - Accuracy improved > 50%
 */
function shouldDispatchImmediately(
  newPos: UserPosition,
  lastPos: UserPosition | null
): boolean {
  if (!lastPos) return true

  // Distance threshold
  if (haversineDistance(lastPos, newPos) > DISTANCE_THRESHOLD_M) return true

  // Accuracy improvement threshold (only if both have accuracy)
  if (lastPos.accuracy && newPos.accuracy) {
    const improvement = (lastPos.accuracy - newPos.accuracy) / lastPos.accuracy
    if (improvement > ACCURACY_IMPROVEMENT_THRESHOLD) return true
  }

  return false
}

/**
 * GUARDRAIL: Defensive time check for paused state
 * Mobile Safari throttles timers when backgrounded, so we can't rely on timers alone
 */
function checkPausedDegradation(
  state: GeoState,
  dispatch: AppDispatch
): boolean {
  if (state.status === "paused" && state.pausedAt) {
    if (nowMillis() - state.pausedAt > MAX_PAUSED_AGE_MS) {
      dispatch(geolocationSlice.actions.pausedDegraded())
      return true
    }
  }
  return false
}

/**
 * GUARDRAIL: Defensive time check for restored state
 */
function checkRestoredExpiration(
  state: GeoState,
  dispatch: AppDispatch
): boolean {
  if (state.status === "restored" && state.position) {
    if (nowMillis() - state.position.timestamp > MAX_RESTORE_AGE_MS) {
      webStorage.removeItem("mindtrip.userPosition", { type: "session" })
      dispatch(geolocationSlice.actions.restoredExpired())
      return true
    }
  }
  return false
}

const DEFAULT_POLICY: WatchPolicy = { pauseOnHidden: true }

// geo/startRequested: Check permission, start watch
geoMiddleware.startListening({
  actionCreator: geoStartRequested,
  effect: (action, { dispatch, getState }) => {
    if (!isClientSide()) return

    const state = (getState() as RootState).geolocation
    const { reason, policy: policyOverride } = action.payload
    const policy: WatchPolicy = { ...DEFAULT_POLICY, ...policyOverride }

    // Don't start if already watching
    if (state.watchId !== null) return

    // Permission gate
    if (state.permission === "denied") {
      dispatch(
        geolocationSlice.actions.watchErrored({
          code: "PERMISSION_DENIED",
          message: "Location permission denied",
          at: nowMillis(),
        })
      )
      return
    }

    // GUARDRAIL: Resume ONLY from paused, never from error
    // This prevents auto-retry storms on flaky GPS
    if (reason === "resume") {
      if (state.status !== "paused") return // strict: resume only from paused
      if (state.permission !== "granted") return
    }

    // Check browser support
    if (!("geolocation" in navigator)) return

    clearTimers()
    lastDispatchedPosition = null

    const startedAt = nowMillis()

    currentWatchId = geolocationSource.watchPosition(
      (position) => {
        // Clear startup timeout on any fix
        if (startupTimeoutId) {
          clearTimeout(startupTimeoutId)
          startupTimeoutId = null
        }

        const pos: UserPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          timestamp: position.timestamp,
        }

        // A pretend fix (staff debug) is never persisted: restored on reload
        // it would pass for the device's own.
        const persist = !geolocationSource.isPretending()

        if (shouldDispatchImmediately(pos, lastDispatchedPosition)) {
          lastDispatchedPosition = pos
          dispatch(geolocationSlice.actions.positionUpdated(pos))

          // Persist (throttled)
          if (persist && !persistThrottleId) {
            webStorage.setJSON("mindtrip.userPosition", pos, {
              type: "session",
            })
            persistThrottleId = setTimeout(() => {
              persistThrottleId = null
            }, THROTTLE_MS)
          }
        } else {
          // Time-based throttle
          if (!dispatchThrottleId) {
            dispatchThrottleId = setTimeout(() => {
              lastDispatchedPosition = pos
              dispatch(geolocationSlice.actions.positionUpdated(pos))
              dispatchThrottleId = null
            }, THROTTLE_MS)
          }
          // Persist (throttled)
          if (persist && !persistThrottleId) {
            webStorage.setJSON("mindtrip.userPosition", pos, {
              type: "session",
            })
            persistThrottleId = setTimeout(() => {
              persistThrottleId = null
            }, THROTTLE_MS)
          }
        }
      },
      (error) => {
        // IMPORTANT: Clear watch BEFORE dispatching error
        if (currentWatchId !== null) {
          geolocationSource.clearWatch(currentWatchId)
          currentWatchId = null
        }
        clearTimers()

        const code =
          error.code === 1
            ? "PERMISSION_DENIED"
            : error.code === 2
              ? "POSITION_UNAVAILABLE"
              : "TIMEOUT"

        // Only clear storage on permission denied
        if (code === "PERMISSION_DENIED") {
          webStorage.removeItem("mindtrip.userPosition", { type: "session" })
        }

        dispatch(
          geolocationSlice.actions.watchErrored({
            code,
            message: error.message,
            at: nowMillis(),
          })
        )
      },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 10000 }
    )

    dispatch(
      geolocationSlice.actions.watchStarted({
        watchId: currentWatchId,
        startedAt,
        policy,
      })
    )

    // No fix timer until permission is granted (prompt dialog can take longer).
    if (
      state.permission === "granted" &&
      shouldArmStartupTimeout(getState as () => RootState)
    ) {
      armStartupTimeout(dispatch as AppDispatch)
    }
  },
})

geoMiddleware.startListening({
  actionCreator: geolocationSlice.actions.permissionUpdated,
  effect: (action, listenerApi) => {
    if (action.payload !== "granted") return
    if (currentWatchId === null) return
    const getState = listenerApi.getState as () => RootState
    if (!shouldArmStartupTimeout(getState)) return
    armStartupTimeout(listenerApi.dispatch as AppDispatch)
  },
})

// geo/stopRequested: Clear watch + optionally clear storage
geoMiddleware.startListening({
  actionCreator: geoStopRequested,
  effect: (action, { dispatch, getState }) => {
    if (!isClientSide()) return

    const state = (getState() as RootState).geolocation
    const reason = action.payload

    // Clear the actual watch
    if (currentWatchId !== null) {
      geolocationSource.clearWatch(currentWatchId)
      currentWatchId = null
    }
    clearTimers()

    if (
      reason === "hidden" &&
      state.position &&
      state.watchPolicy?.pauseOnHidden !== false
    ) {
      // Pause with degradation timer
      const pausedAt = nowMillis()
      dispatch(geolocationSlice.actions.watchPaused(pausedAt))

      // Start degradation timer
      pausedDegradationId = setTimeout(() => {
        dispatch(geolocationSlice.actions.pausedDegraded())
        pausedDegradationId = null
      }, MAX_PAUSED_AGE_MS)
    } else if (reason === "logout") {
      webStorage.removeItem("mindtrip.userPosition", { type: "session" })
      dispatch(geolocationSlice.actions.fullReset())
    } else {
      // reason === "user" OR hidden with pauseOnHidden=false and no position
      webStorage.removeItem("mindtrip.userPosition", { type: "session" })
      dispatch(geolocationSlice.actions.watchStopped())
    }
  },
})

// geo/visibilityChanged: Pause on hidden, resume on visible
geoMiddleware.startListening({
  actionCreator: geoVisibilityChanged,
  effect: (action, { dispatch, getState }) => {
    if (!isClientSide()) return

    const state = (getState() as RootState).geolocation
    const visibility = action.payload

    if (visibility === "hidden") {
      if (
        (state.status === "watching" || state.status === "acquiring") &&
        state.watchPolicy?.pauseOnHidden !== false
      ) {
        dispatch(geoStopRequested("hidden"))
      }
    } else if (visibility === "visible") {
      // Clear any pending degradation timer
      if (pausedDegradationId) {
        clearTimeout(pausedDegradationId)
        pausedDegradationId = null
      }

      // GUARDRAIL: Defensive time check (mobile Safari throttles timers when backgrounded)
      const degraded = checkPausedDegradation(state, dispatch as AppDispatch)
      if (!degraded) {
        checkRestoredExpiration(state, dispatch as AppDispatch)
      }

      // Re-fetch state after potential degradation
      const currentState = (getState() as RootState).geolocation

      // GUARDRAIL: Resume ONLY from paused, never from error
      if (
        currentState.status === "paused" &&
        currentState.permission === "granted"
      ) {
        dispatch(
          geoStartRequested({
            reason: "resume",
            policy: currentState.watchPolicy ?? undefined,
          })
        )
      }
    }
  },
})

// Sync position to module for axios header
geoMiddleware.startListening({
  actionCreator: geolocationSlice.actions.positionUpdated,
  effect: (action) => {
    const pos = action.payload
    setCurrentPosition({
      latitude: pos.latitude,
      longitude: pos.longitude,
      accuracy: pos.accuracy,
      timestamp: pos.timestamp,
    })
  },
})

// Also sync restored position on init
geoMiddleware.startListening({
  actionCreator: geolocationSlice.actions.storageRestored,
  effect: (action) => {
    const pos = action.payload.position
    if (pos) {
      setCurrentPosition({
        latitude: pos.latitude,
        longitude: pos.longitude,
        accuracy: pos.accuracy,
        timestamp: pos.timestamp,
      })
    }
  },
})

// Drop the cached initGeo promise so the next caller re-runs init.
geoMiddleware.startListening({
  actionCreator: geolocationSlice.actions.fullReset,
  effect: () => {
    resetInitGeoMemo()
  },
})

/**
 * Export for testing - reset all middleware state
 */
export function __resetGeoMiddlewareForTesting() {
  if (currentWatchId !== null && isClientSide()) {
    geolocationSource.clearWatch(currentWatchId)
    currentWatchId = null
  }
  clearTimers()
  lastDispatchedPosition = null
}
